import { AxiosError } from "axios";
import { useCallback, useEffect, useMemo, useState } from "react";
import { useParams } from "react-router-dom";
import { ServiceTypes, UnionServiceType } from "src/api/adminMemo/adminmemo-types";
import { getContractApply } from "src/api/contract/contract-api";
import { getMediaFileListAsync } from "src/api/file/file-api";

import { deleteGuideAsync, getGuideListAsync } from "src/api/guide/guide-api";
import { ServiceUserGuide, UserGuideModel, UserGuideTypeMap } from "src/api/guide/guide-types";
import { useApiOperation } from "src/api/hooks";
import { getProviderList } from "src/api/provider/provider-api";
import { ProviderModel } from "src/api/provider/provider-types";
import { MediaFile } from "src/api/public-types";
import { BaseButton } from "src/components";
import AdminMemoV3 from "src/components/AdminMemoV3/AdminMemoV3";
import BaseNewTabLink from "src/components/BaseNewTabLink";
import GoToListButton from "src/components/GoToListButton";
import BaseSectionTitle from "src/components/layout/BaseSectionTitle";
import MetaTag from "src/components/layout/MetaTag";
import MarkdownViewer from "src/components/MarkdownViewer";
import BaseFileUpload from "src/components/mediaFile/BaseFileUpload";
import RegistrationInformation from "src/components/RegistrationInformation";
import { usePartnerAuthority } from "src/hooks/usePartnerAuthority";
import useNavigate from "src/hooks/usePartnerNavigate";
import { PagePath } from "src/pages/product/details";
import { useModal } from "src/recoil/modalState/hook";
import { useToast } from "src/recoil/toast/hook";
import { ViewYmdFormat } from "src/utils";
import { userGuideTargetText } from "../../guide-types";

const BasicInfoDetail = () => {
  const { id } = useParams();
  const [guide, setGuide] = useState<UserGuideModel>();
  const [provider, setProvider] = useState<ProviderModel>();
  const navigate = useNavigate();

  const { setBaseModal } = useModal();
  const { openToast } = useToast();
  const { isAuthority } = usePartnerAuthority();

  const { executeAsync: getProvider } = useApiOperation(getProviderList);

  const { executeAsync: getGuideList } = useApiOperation(getGuideListAsync);

  const { executeAsync: deleteGuide } = useApiOperation(deleteGuideAsync, { noErrorModal: true });

  // 신청계약 목록 조회
  const { executeAsync: getContractApplyAsync } = useApiOperation(getContractApply);

  // 미디어 목록
  const { executeAsync: getMediaFileList } = useApiOperation(getMediaFileListAsync, { noLoading: true });

  // 상담관리 미디어 파일
  const [mediaFiles, setMediaFiles] = useState<MediaFile[]>([]);

  // 적용대상 텍스트
  const targetText = useMemo(() => {
    return userGuideTargetText(guide?.targetsList || []);
  }, [guide]);

  /** 첨부파일 목록 api*/
  const fetchMediaList = useCallback(
    async (data: { serviceId: number; serviceType: UnionServiceType }) => {
      const contentsList = [{ serviceId: data.serviceId, serviceType: data?.serviceType }];

      const response = await getMediaFileList({ contentsList });
      setMediaFiles(response.data.data.content);
    },
    [getMediaFileList],
  );

  useEffect(() => {
    const fetchApi = async (guideId: string) => {
      const response = await getGuideList({ page: 0, size: 1, idList: guideId });
      const _guide: UserGuideModel | undefined = response?.data?.data?.content?.[0];

      if (!_guide) {
        return;
      }

      setGuide(response?.data?.data?.content?.[0]);

      getProvider({ providerId: _guide.providerId }).then((data) => setProvider(data?.data?.data?.content?.[0]));

      if (_guide.type === "USER_GUIDE_ETC") {
        fetchMediaList({ serviceId: Number(guideId), serviceType: ServiceTypes.SERVICE_USER_GUIDE });
      }
    };

    if (id) {
      fetchApi(id);
    }
  }, [id]);

  const showDeleteBlockedModal = async (errorData: string) => {
    const usedServiceGuideList: ServiceUserGuide[] = JSON.parse(errorData);

    const usedProductList = usedServiceGuideList
      ?.filter((data) => data?.serviceType === "SERVICE_PRODUCT")
      .map((data) => data?.serviceId)
      .join(", ");

    const usedContractList: string[] = [];

    const contractManageIds = usedServiceGuideList
      ?.filter((data) => data.serviceType === "SERVICE_CONTRACT_MANAGE")
      .map((data) => data.serviceId)
      .join(",");

    if (contractManageIds.length > 0) {
      const contracts = await getContractApplyAsync({ page: 0, size: 999, contractManageIds })
        .then(({ data }) => data.data.content)
        .catch(() => []);
      contracts?.forEach((contract) => usedContractList.push(String(contract.contractId)));
    }

    setBaseModal({
      isOpen: true,
      title: `사용중인 항목이 있어\n삭제할 수 없습니다.`,
      children: (
        <div className="flex-d flex-row">
          <p>아래 항목을 먼저 처리한 후 다시 시도해 주세요.</p>
          <div className="w-100 text-left py10 px15 mt20 overflow-scroll" style={{ backgroundColor: "#F2F4F6", maxHeight: 250 }}>
            {usedProductList.length > 0 && (
              <div>
                • 상품: <span>{usedProductList}</span>
              </div>
            )}
            {usedContractList.length > 0 && (
              <div>
                • 신청/계약: <span>{usedContractList.join(", ")}</span>
              </div>
            )}
          </div>
        </div>
      ),
      btnRightTitle: "확인",
    });
  };

  const handleDeleteGuide = async () => {
    try {
      const { data } = await deleteGuide({ idList: id! });

      // 삭제된 카운트가 비면 실제로 지워진 것이 없다
      if (!data?.data?.content) {
        setBaseModal({ isOpen: false });
        openToast({ content: "정상적으로 삭제 되지 않았습니다.", type: "FAIL" });
        return;
      }
    } catch (error) {
      const errorMeta = error instanceof AxiosError ? error.response?.data?.meta : undefined;

      // 상품 or 계약에서 사용중인 경우 삭제 불가 에러
      if (errorMeta?.errorCode === "eET0517") {
        await showDeleteBlockedModal(errorMeta.errorData);
      } else {
        setBaseModal({ isOpen: false });
      }

      openToast({ content: "정상적으로 삭제 되지 않았습니다.", type: "FAIL" });
      return;
    }

    openToast({ content: "정상적으로 삭제 되었습니다." });
    navigate(PagePath.guide.list);
    setBaseModal({ isOpen: false });
  };

  return (
    <>
      <MetaTag subTitle={guide?.subject || ""} />
      <div className="contents-container__scroll">
        <div className="contents-container__wrap price-page">
          <article className="contents-container__wrap-article">
            <BaseSectionTitle title={"기본정보"} />

            <section className="contents-container__grid">
              <div className="contents-container__grid-index">
                <p>유형</p>
              </div>
              <div className="contents-container__grid-contents">
                <div className="minmax400">
                  <span>{guide?.type ? UserGuideTypeMap[guide?.type] : "-"}</span>
                </div>
              </div>
            </section>

            {/* 기본정보 */}
            <section className="contents-container__grid">
              <div className="contents-container__grid-index">
                <p>프로바이더</p>
              </div>
              <div className="contents-container__grid-contents">
                {/* 프로바이더명(정산정보 ID:{id}) - ID 선택 시 정산정보 상세로 이동 */}
                {/* 첫 번째 자식 div 는 grid 공통 스타일(column + 세로 가운데)이 적용되므로, 한 줄 배치는 안쪽 div 에서 처리 */}
                <div>
                  {provider?.providerId ? (
                    <div className="flex-center-start">
                      <span className="no-wrap">{`${provider.providerName || "-"}(정산정보 ID:`}</span>
                      <BaseNewTabLink
                        path={`${PagePath.provider.detail.replace(":id", provider.providerId.toString())}`}
                        value={provider.providerId.toString()}
                      />
                      <span>)</span>
                    </div>
                  ) : (
                    <span>-</span>
                  )}
                </div>
              </div>
            </section>

            <section className="contents-container__grid">
              <div className="contents-container__grid-index">
                <p>적용대상</p>
              </div>
              <div className="contents-container__grid-contents">
                <div className="minmax400">
                  <span>{targetText}</span>
                </div>
              </div>
            </section>

            <section className="contents-container__grid">
              <div className="contents-container__grid-index">
                <p>항목명</p>
              </div>
              <div className="contents-container__grid-contents">
                <div className="minmax400">
                  <span>{guide?.subject ?? "-"}</span>
                </div>
              </div>
            </section>
            {guide?.type !== "USER_GUIDE_WIFI" && (
              // 네트워크 타입이 아닐때만 노출
              <section className="contents-container__grid">
                <div className="contents-container__grid-index">
                  <p>상세내용</p>
                </div>
                <div className="contents-container__grid-contents">
                  <div className="minmax400">
                    {guide?.description ? <MarkdownViewer value={guide?.description} isDisabled></MarkdownViewer> : "-"}
                  </div>
                </div>
              </section>
            )}
            {/* 첨부파일 */}
            {guide?.type === "USER_GUIDE_ETC" && (
              //일반 타입에서만 노출
              <section className="contents-container__grid">
                <div className="contents-container__grid-index">
                  <p>첨부파일</p>
                </div>
                <div className="contents-container__grid-contents">
                  <div className="user-info-container">
                    {mediaFiles.length === 0 ? <span>-</span> : <BaseFileUpload isOnlyView fileList={mediaFiles} />}
                  </div>
                </div>
              </section>
            )}

            {guide?.type === "USER_GUIDE_WIFI" && (
              <section className="contents-container__grid contents-container__1200 mb30">
                <div className="contents-container__grid-index">
                  <p>상세내용</p>
                </div>

                <div className="contents-container__grid-contents">
                  <div className="border-gray mb10">
                    <div className="pa16">
                      <div className="flex-center mb10">
                        <div className="minmax140">
                          <span>위치</span>
                        </div>
                        <div>
                          <span className="font14">{guide?.location ?? "-"}</span>
                        </div>
                      </div>
                      {/* 네트워크 */}
                      <div className="flex-start mb10">
                        <div className="minmax140">
                          <p>
                            네트워크 이름/
                            <br />
                            비밀번호
                          </p>
                        </div>
                        <table className="inner-table" width="100%">
                          <thead>
                            <tr>
                              <th>
                                <span>No</span>
                              </th>
                              <th>
                                <span>네트워크 이름</span>
                              </th>
                              <th>
                                <span>비밀번호</span>
                              </th>
                            </tr>
                          </thead>
                          <tbody>
                            {guide?.wifiList &&
                              guide.wifiList
                                .sort((a, b) => Number(a.id || 0) - Number(b.id || 0))
                                .map((wifi, index: number) => (
                                  <tr key={index.toString()}>
                                    <td width="100">
                                      <div className="flex-center-center">
                                        <span>{index + 1}</span>
                                      </div>
                                    </td>
                                    <td width="220">
                                      <div className="flex-center-center">
                                        <span className="font14">{wifi.wifiName ?? "-"}</span>
                                      </div>
                                    </td>
                                    <td width="auto">
                                      <div className="flex-center-center">
                                        <span className="font14">{wifi.wifiPassword ?? "-"}</span>
                                      </div>
                                    </td>
                                  </tr>
                                ))}
                          </tbody>
                        </table>
                      </div>
                      <div className="flex-center mb10">
                        <div className="minmax140">
                          <span>설명</span>
                        </div>
                        <div>
                          <span className="font14 pre-formatted">{guide?.description ?? "-"}</span>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </section>
            )}
          </article>

          <RegistrationInformation data={guide} dateFormat={ViewYmdFormat.FULL} />

          <AdminMemoV3 serviceId={Number(id)} serviceType={ServiceTypes.SERVICE_USER_GUIDE} />
        </div>
      </div>
      <div className="contents-container__btn-wrap">
        <div className="left-area d-flex">
          <GoToListButton />
        </div>
        <div className="left-area d-flex">
          {isAuthority("d") && (
            <BaseButton
              title="삭제"
              className="color-white size-large mr10"
              onClick={() => {
                setBaseModal({
                  isOpen: true,
                  title: "삭제하시겠습니까?",
                  btnLeftTitle: "취소",
                  btnRightTitle: "확인",
                  onClick: () => {
                    handleDeleteGuide();
                  },
                });
              }}
            />
          )}
          {isAuthority("w") && (
            <BaseButton
              title="수정"
              className="size-large"
              onClick={() => {
                const formPath = `${PagePath.guide.form}?id=${id}`;
                navigate(formPath);
              }}
            />
          )}
        </div>
      </div>
    </>
  );
};

export default BasicInfoDetail;
